import { FC, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { useSetRecoilState } from 'recoil';

import {
  createAcqMsg,
  deleteAcqMsg,
  deleteAcqMsgByMember,
  getAcqMsgList,
  updateAcqMsg,
} from '@shared/api/acqMsg/acqMsg.controller';
import { AcqMsg } from '@shared/api/acqMsg/acqMsg.interface';
import { Icon } from '@shared/components/icons';
import Modal from '@shared/components/Modal';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { useQuery } from 'react-query';

const PAGE_SIZE = 5;
const PREVIEW_COUNT = 3;
const FOLD_BUTTON_CLASS =
  '-my-1.5 flex min-h-[44px] flex-1 items-center justify-center gap-1 text-sm text-gray-600 hover:text-gray-900';

const CONDOLENCE_PHRASES = [
  '삼가 故人의 冥福을 빕니다',
  '삼가 고인의 명복을 빕니다',
  '천국에서 편히 쉬소서',
  '주님의 위로가 함께 하소서',
  '극락왕생발원',
];

interface Props {
  feventId: string;
  memberId?: string;
  bugoBrandName?: string;
}

const ACQ_MSG_FORM_MODAL_ID = 'acqMsgFormModal';

export const AcqMsgSection: FC<Props> = function AcqMsgSection({
  feventId,
  memberId,
  bugoBrandName,
}) {
  const { ChevronDown, ChevronUp, Pencil } = Icon();
  const [visibleCount, setVisibleCount] = useState(PREVIEW_COUNT);
  const sectionRef = useRef<HTMLDivElement>(null);
  const moreButtonRef = useRef<HTMLButtonElement>(null);
  const restorePositionRef = useRef(false);
  const formTitleRef = useRef<HTMLHeadingElement>(null);
  const listId = useId();
  const setFormModalOpen = useSetRecoilState(simpleOpenAtomFamily(ACQ_MSG_FORM_MODAL_ID));

  //* 캐시 필수: 화환 갔다가 뒤로 오면 페이지가 다시 마운트되는데, 그때마다 빈 상태에서 받으면 조의글 높이만큼
  //* 아래 섹션이 튀고(약 300px) 복원된 스크롤 위치도 짧아진 문서에 잘린다(2026-11-02 사용자, 실측).
  const {
    data,
    isLoading,
    refetch: fetchMessages,
  } = useQuery<AcqMsg[]>(['acqMsg', feventId], () => getAcqMsgList(feventId), {
    retry: false,
    refetchOnWindowFocus: false,
  });
  const messages = useMemo(() => (data ?? []).filter((m) => !m.isBan), [data]);

  const brandFixedMsg: AcqMsg | null = useMemo(() => {
    if (!bugoBrandName) return null;
    return {
      _id: '__brand__',
      name: `${bugoBrandName} 임직원 일동`,
      phoneNumber: '',
      message: '삼가 고인의 명복을 빕니다.',
      password: '',
      fevent: feventId,
      createdAt: '',
      updatedAt: '',
    };
  }, [bugoBrandName, feventId]);

  const allMessages = useMemo(() => {
    if (brandFixedMsg) return [...messages, brandFixedMsg];
    return messages;
  }, [messages, brandFixedMsg]);

  const visibleMessages = allMessages.slice(0, visibleCount);
  const remainingCount = Math.max(0, allMessages.length - visibleCount);
  const expanded = visibleCount > PREVIEW_COUNT;

  useLayoutEffect(() => {
    if (!restorePositionRef.current) return;
    restorePositionRef.current = false;
    moreButtonRef.current?.focus({ preventScroll: true });
    // 접기로 목록이 짧아져도 사용자가 보고 있던 조의글 영역을 놓치지 않도록 한다.
    if (sectionRef.current && sectionRef.current.getBoundingClientRect().top < 0) {
      sectionRef.current.scrollIntoView({ block: 'start' });
    }
  }, [visibleCount]);

  if (isLoading) return null;

  return (
    <div ref={sectionRef} className="scroll-mt-4 bg-white px-2">
      <div className="flex items-center justify-between gap-2 pb-1 pt-3">
        <div className="flex min-w-0 items-center gap-1">
          <Pencil className="wh-8 shrink-0" />
          <p className="font-bold text-gray-500">조의글 남기기</p>
        </div>
        {/* 터치 영역은 44px을 유지하되 음수 마진으로 헤더 높이는 늘리지 않는다. */}
        <button
          type="button"
          className="-my-1.5 flex min-h-[44px] shrink-0 items-center pr-2"
          onClick={() => setFormModalOpen(true)}
        >
          <span className="rounded-md border bg-white px-2 py-0.5 text-[14px] font-bold theme-text-main theme-border-main">
            조의글 작성하기
          </span>
        </button>
      </div>

      <div className="px-2 pb-2">
        <div id={listId} className="space-y-1">
          {allMessages.length === 0 && (
            <p className="py-2 text-center text-sm text-gray-400">
              아직 조의글이 없습니다
            </p>
          )}
          {visibleMessages.map((msg) => (
            <AcqMsgItem
              key={msg._id}
              msg={msg}
              memberId={memberId}
              onRefresh={fetchMessages}
            />
          ))}
        </div>

        {allMessages.length > PREVIEW_COUNT && (
          <div className="mt-1 flex items-center gap-2 border-t border-gray-100 pt-1">
            {remainingCount > 0 && (
              <button
                ref={moreButtonRef}
                type="button"
                aria-controls={listId}
                aria-expanded={expanded}
                className={FOLD_BUTTON_CLASS}
                onClick={() =>
                  setVisibleCount((count) =>
                    Math.min(count + PAGE_SIZE, allMessages.length),
                  )
                }
              >
                조의글 더보기 ({remainingCount})
                <ChevronDown aria-hidden="true" className="h-3.5 w-3.5" />
              </button>
            )}
            {expanded && (
              <button
                type="button"
                aria-controls={listId}
                aria-expanded={expanded}
                className={FOLD_BUTTON_CLASS}
                onClick={() => {
                  restorePositionRef.current = true;
                  setVisibleCount(PREVIEW_COUNT);
                }}
              >
                접기
                <ChevronUp aria-hidden="true" className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* 첫 입력칸에 자동 포커스되면 키보드가 폼을 가려서 제목으로 받는다 — 키보드는 입력칸을 누를 때 올라옴(2026-11-02 사용자) */}
      <Modal
        openId={ACQ_MSG_FORM_MODAL_ID}
        position="start"
        className="w-full max-w-md"
        initialFocus={formTitleRef}
      >
        <h3
          ref={formTitleRef}
          tabIndex={-1}
          className="mb-4 text-base font-bold text-gray-700 outline-none"
        >
          조의글 남기기
        </h3>
        <AcqMsgForm
          feventId={feventId}
          onSuccess={() => {
            setFormModalOpen(false);
            setVisibleCount(PREVIEW_COUNT);
            fetchMessages();
            history.back();
          }}
          onCancel={() => {
            setFormModalOpen(false);
            history.back();
          }}
        />
      </Modal>
    </div>
  );
};

const AcqMsgItem: FC<{
  msg: AcqMsg;
  memberId?: string;
  onRefresh: () => void;
}> = ({ msg, memberId, onRefresh }) => {
  const isFixed = msg._id === '__brand__';

  const [showActions, setShowActions] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [actionMode, setActionMode] = useState<'edit' | 'delete' | null>(null);
  const [editName, setEditName] = useState(msg.name);
  const [editMessage, setEditMessage] = useState(msg.message);
  const [error, setError] = useState('');

  const timeAgo = useMemo(() => {
    if (!msg.createdAt) return '';
    const diff = Date.now() - new Date(msg.createdAt).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return '방금 전';
    if (mins < 60) return `${mins}분 전`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}시간 전`;
    const days = Math.floor(hours / 24);
    return `${days}일 전`;
  }, [msg.createdAt]);

  const handlePasswordAction = async () => {
    setError('');
    try {
      if (actionMode === 'delete') {
        await deleteAcqMsg(msg._id, passwordInput);
      } else if (actionMode === 'edit') {
        await updateAcqMsg(msg._id, {
          password: passwordInput,
          name: editName,
          message: editMessage,
        });
      }
      setActionMode(null);
      setPasswordInput('');
      onRefresh();
    } catch {
      setError('비밀번호가 일치하지 않습니다');
    }
  };

  const handleMemberDelete = async () => {
    if (!memberId) return;
    const confirmed = window.confirm(
      '상주님께서 조의글을 직접 관리(삭제)할 수 있는 기능입니다.\n해당 조의글을 삭제하시겠습니까?',
    );
    if (!confirmed) return;
    try {
      await deleteAcqMsgByMember(msg._id, memberId);
      onRefresh();
    } catch {
      setError('삭제에 실패했습니다');
    }
  };

  if (msg.isSecret && !memberId) {
    return (
      <div className="rounded-md bg-gray-50 px-3 py-1.5">
        <p className="text-sm text-gray-400">🔒 비밀글입니다</p>
      </div>
    );
  }

  return (
    <div className="rounded-md bg-gray-50 px-3 py-2">
      {msg.isSecret && memberId && (
        <p className="mb-1 text-xs text-gray-400">🔒 비밀글</p>
      )}
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          {actionMode === 'edit' ? (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder="보내시는 분"
                  className="min-w-0 flex-1 rounded border border-gray-300 px-2 py-1 text-sm font-semibold"
                />
                {timeAgo && (
                  <span className="shrink-0 text-xs text-gray-400">{timeAgo}</span>
                )}
              </div>
              <textarea
                value={editMessage}
                onChange={(e) => setEditMessage(e.target.value)}
                className="w-full rounded border border-gray-300 p-1.5 text-sm"
                rows={2}
              />
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between gap-2">
                <span className="min-w-0 break-keep text-sm font-semibold text-gray-600 [overflow-wrap:anywhere]">
                  {msg.name}
                </span>
                {timeAgo && (
                  <span className="shrink-0 text-xs text-gray-400">{timeAgo}</span>
                )}
              </div>
              <p className="mt-0.5 whitespace-pre-line break-keep text-sm leading-5 text-gray-600 [overflow-wrap:anywhere]">
                {msg.message}
              </p>
            </>
          )}
        </div>
        {!isFixed && !actionMode && (
          <button
            className="ml-2 flex shrink-0 items-center justify-center rounded-full p-1 text-gray-500 active:bg-gray-200"
            onClick={() => setShowActions(!showActions)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 16 16"
              fill="currentColor"
              className="h-4 w-4"
            >
              <path d="M8 2a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM8 6.5a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM9.5 12.5a1.5 1.5 0 1 0-3 0 1.5 1.5 0 0 0 3 0Z" />
            </svg>
          </button>
        )}
      </div>

      {showActions && !actionMode && (
        <div className="mt-1.5 flex justify-end gap-2">
          <button
            className="text-xs text-gray-500 underline"
            onClick={() => {
              setActionMode('edit');
              setShowActions(false);
            }}
          >
            수정
          </button>
          <button
            className="text-xs text-gray-500 underline"
            onClick={() => {
              setActionMode('delete');
              setShowActions(false);
            }}
          >
            삭제
          </button>
          {memberId && (
            <button
              className="text-xs text-red-400 underline"
              onClick={handleMemberDelete}
            >
              관리 삭제
            </button>
          )}
        </div>
      )}

      {actionMode && (
        <div className="mt-2 space-y-1.5">
          <div className="flex min-w-0 items-center gap-2">
            <input
              type="password"
              inputMode="numeric"
              maxLength={4}
              placeholder="비밀번호 4자리"
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value.replace(/\D/g, ''))}
              className="min-w-0 flex-1 rounded border border-gray-300 px-2 py-1.5 text-sm"
            />
            <button
              className="shrink-0 rounded bg-gray-700 px-3 py-1.5 text-sm text-white disabled:opacity-40"
              onClick={handlePasswordAction}
              disabled={passwordInput.length !== 4}
            >
              {actionMode === 'edit' ? '수정' : '삭제'}
            </button>
            <button
              className="shrink-0 text-sm text-gray-400"
              onClick={() => {
                setActionMode(null);
                setPasswordInput('');
                setError('');
                setEditName(msg.name);
                setEditMessage(msg.message);
              }}
            >
              취소
            </button>
          </div>
          {error && <p className="text-xs text-red-500">{error}</p>}
        </div>
      )}
    </div>
  );
};

const AcqMsgForm: FC<{
  feventId: string;
  onSuccess: () => void;
  onCancel: () => void;
}> = ({ feventId, onSuccess, onCancel }) => {
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [message, setMessage] = useState('');
  const [password, setPassword] = useState('');
  const [isSecret, setIsSecret] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showPhrases, setShowPhrases] = useState(false);

  const canSubmit =
    name.trim() && phoneNumber.trim() && message.trim() && password.length === 4;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    try {
      await createAcqMsg({
        name: name.trim(),
        phoneNumber: phoneNumber.trim(),
        message: message.trim(),
        password,
        fevent: feventId,
        isSecret,
      });
      onSuccess();
    } catch {
      setError('조의글 등록에 실패했습니다');
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    'w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-gray-500 focus:outline-none';

  return (
    <div className="w-full space-y-3">
      <input
        className={inputClass}
        placeholder="보내시는 분 (예: ○○회사 대표 홍길동)"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <input
        className={inputClass}
        placeholder="휴대번호(수정 및 답례 용도)"
        inputMode="tel"
        value={phoneNumber}
        onChange={(e) => setPhoneNumber(e.target.value)}
      />

      <div>
        <div className="flex items-center justify-between">
          <button
            type="button"
            className="mb-1 text-xs text-gray-400 underline"
            onClick={() => setShowPhrases(!showPhrases)}
          >
            {showPhrases ? '추천 문구 닫기' : '추천 문구'}
          </button>
        </div>
        {showPhrases && (
          <div className="mb-2 flex flex-wrap gap-1">
            {CONDOLENCE_PHRASES.map((phrase) => (
              <button
                key={phrase}
                type="button"
                className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs text-gray-600"
                onClick={() => {
                  setMessage(phrase);
                  setShowPhrases(false);
                }}
              >
                {phrase}
              </button>
            ))}
          </div>
        )}
        <textarea
          className={`${inputClass} resize-none`}
          placeholder="조의글을 남겨주세요"
          rows={3}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />
      </div>

      <div className="flex min-w-0 items-center gap-2">
        <input
          className="min-w-0 flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-gray-500 focus:outline-none"
          type="password"
          inputMode="numeric"
          maxLength={4}
          placeholder="비밀번호 4자리"
          value={password}
          onChange={(e) => setPassword(e.target.value.replace(/\D/g, ''))}
        />
        <label className="flex shrink-0 items-center gap-1 text-xs text-gray-500">
          <input
            type="checkbox"
            checked={isSecret}
            onChange={(e) => setIsSecret(e.target.checked)}
          />
          비밀글
        </label>
      </div>
      <p className="text-xs text-gray-400">비밀번호는 수정/삭제 시 필요합니다</p>

      {error && <p className="text-xs text-red-500">{error}</p>}

      <div className="flex min-w-0 gap-2 pt-1">
        <button
          type="button"
          className="shrink-0 rounded-md border border-gray-300 px-4 py-2.5 text-sm text-gray-500"
          onClick={onCancel}
        >
          취소
        </button>
        <button
          type="button"
          className="min-w-0 flex-1 rounded-md py-2.5 text-sm font-semibold text-white disabled:opacity-50 theme-bg-13"
          onClick={handleSubmit}
          disabled={!canSubmit || submitting}
        >
          {submitting ? '등록 중...' : '등록'}
        </button>
      </div>
    </div>
  );
};
